import React, { useState } from 'react';
import { StyleSheet, Text, View, Platform } from 'react-native';
import { FontFamily } from '../../../../../constants/fonts';
import { CrewingVerticalScrollView } from './webScrollStyles';

export interface HoursBarSeries {
  key: string;
  label: string;
  color: string;
}

export interface HoursBarGroup {
  label: string;
  values: Record<string, number>;
}

interface HoursGroupedBarCardProps {
  title: string;
  unitLabel?: string;
  series: HoursBarSeries[];
  groups: HoursBarGroup[];
}

const MIN_BAR_HEIGHT = 180;
const LABEL_HEADROOM = 18;

// Large numbers are abbreviated with a K suffix (e.g. 6423000 -> "6423K") — was previously just
// appending "K" to the raw value without dividing by 1000, mislabeling small values.
const formatK = (value: number) => {
  const rounded = Math.round(value);
  if (Math.abs(rounded) >= 1000) return `${Math.round(rounded / 1000)}K`;
  return rounded.toLocaleString();
};

const HoursGroupedBarCard: React.FC<HoursGroupedBarCardProps> = ({
  title,
  unitLabel = 'hours',
  series,
  groups,
}) => {
  // Check for no data condition
  if (!groups || groups.length === 0 || !series || series.length === 0) {
    return (
      <View style={styles.card}>
        <View style={styles.headerRow}>
          <Text style={styles.title}>{title}</Text>
          <Text style={styles.unitLabel}>{unitLabel}</Text>
        </View>
        <View style={styles.emptyState}>
          <Text style={styles.emptyText}>No hours data available</Text>
        </View>
      </View>
    );
  }

  // Backend can return groups with every series value at 0 — treat that as no data too
  const hasNonZeroValue = groups.some((group) => series.some((item) => (group.values[item.key] ?? 0) !== 0));
  if (!hasNonZeroValue) {
    return (
      <View style={styles.card}>
        <View style={styles.headerRow}>
          <Text style={styles.title}>{title}</Text>
          <Text style={styles.unitLabel}>{unitLabel}</Text>
        </View>
        <View style={styles.emptyState}>
          <Text style={styles.emptyText}>No hours data available</Text>
        </View>
      </View>
    );
  }

  const maxValue = Math.max(
    1,
    ...groups.flatMap((group) => series.map((item) => group.values[item.key] ?? 0)),
  );
  // Keep visual headroom so tallest bars do not touch/overflow the chart top.
  const scaledMaxValue = maxValue * 1.12;

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((ratio) => Math.round(scaledMaxValue * ratio));

  const [viewportHeight, setViewportHeight] = useState(0);
  // Fill whatever vertical space the card actually has instead of a fixed 180px.
  const MAX_BAR_HEIGHT = Math.max(MIN_BAR_HEIGHT, viewportHeight - 22);

  const horizontalEnabled = Platform.OS === 'ios';
  const contentMinWidth = Math.max(0, groups.length * 220);

  
  return (
    <View style={styles.card}>
      <View style={styles.headerRow}>
        <Text style={styles.title}>{title}</Text>
        <Text style={styles.unitLabel}>{unitLabel}</Text>
      </View>

      <View style={styles.legendRow}>
        {series.map((item) => (
          <View key={item.key} style={styles.legendItem}>
            <View style={[styles.legendDot, { backgroundColor: item.color }]} />
            <Text style={styles.legendText}>{item.label}</Text>
          </View>
        ))}
      </View>

      <View
        style={styles.chartViewport}
        onLayout={(event) => {
          const nextHeight = event.nativeEvent.layout.height;
          if (nextHeight > 0 && Math.abs(nextHeight - viewportHeight) > 1) setViewportHeight(nextHeight);
        }}
      >
        <CrewingVerticalScrollView
          nestedScrollEnabled
          horizontal={horizontalEnabled}
          showsVerticalScrollIndicator={!horizontalEnabled}
          showsHorizontalScrollIndicator={horizontalEnabled}
          contentContainerStyle={horizontalEnabled ? { minWidth: contentMinWidth } : undefined}
        >
          <View style={styles.chartRow}>
            <View style={[styles.yAxisCol, { height: MAX_BAR_HEIGHT + 22 }]}>
              {[...yTicks].reverse().map((tick) => (
                <Text key={tick} style={styles.yAxisText}>{tick === 0 ? '0' : formatK(tick)}</Text>
              ))}
            </View>

            <View style={[styles.plotArea, { height: MAX_BAR_HEIGHT + 22 }]}>
              {[0, 1, 2, 3].map((line) => (
                <View key={line} style={[styles.gridLine, { top: `${line * 25}%` }]} />
              ))}

              <View style={[styles.groupsRow, horizontalEnabled ? styles.groupsRowScroll : null]}> 
                {groups.map((group) => (
                  <View key={group.label} style={[styles.groupCol, horizontalEnabled ? styles.groupColScrollable : null]}>
                    <View style={[styles.groupBars, { height: MAX_BAR_HEIGHT }]}>
                      {series.map((item) => {
                        const value = group.values[item.key] ?? 0;
                        const barHeight = Math.max(2, (value / scaledMaxValue) * (MAX_BAR_HEIGHT - LABEL_HEADROOM));
                        return (
                          <View key={`${group.label}-${item.key}`} style={styles.barSlot}>
                            <Text style={[styles.barValue, { color: item.color }]}>{formatK(value)}</Text>
                            <View
                              style={[
                                styles.bar,
                                {
                                  height: barHeight,
                                  backgroundColor: item.color,
                                },
                              ]}
                            />
                          </View>
                        );
                      })}
                    </View>
                    <Text style={styles.groupLabel}>{group.label}</Text>
                  </View>
                ))}
              </View>
            </View>
          </View>
        </CrewingVerticalScrollView>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 16,
    flex: 1,
    minHeight: 320,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
    flexWrap: 'wrap',
    width: '100%',
  },
  title: {
    fontSize: 16,
    fontFamily: FontFamily.bold,
    color: '#1A1D21',
    fontWeight: '700',
  },
  unitLabel: {
    fontSize: 10,
    fontFamily: FontFamily.regular,
    color: '#838383',
  },
  legendRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    marginBottom: 10,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E3E9F3',
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  legendDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  legendText: {
    fontSize: 11,
    color: '#6B7280',
  },
  chartViewport: {
    flex: 1,
  },
  chartRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
  },
  yAxisCol: {
    width: 42,
    justifyContent: 'space-between',
    paddingRight: 6,
  },
  yAxisText: {
    fontSize: 10,
    color: '#6B7280',
    textAlign: 'right',
  },
  plotArea: {
    flex: 1,
    position: 'relative',
    minWidth: '100%',
  },
  gridLine: {
    position: 'absolute',
    left: 0,
    right: 0,
    borderTopWidth: 1,
    borderTopColor: '#ECEFF4',
  },
  groupsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    height: '100%',
    paddingBottom: 2,
    width: '100%',
  },
  groupsRowScroll: {
    justifyContent: 'flex-start',
  },
  groupColScrollable: {
    flex: 0,
    minWidth: 160,
  },
  groupCol: {
    alignItems: 'center',
    flex: 1,
    minWidth: 0,
    marginHorizontal: 12,
  },
  groupBars: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  barSlot: {
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginHorizontal: 1,
  },
  barValue: {
    fontSize: 10,
    fontWeight: '700',
    marginBottom: 2,
  },
  bar: {
    width: 42,
    borderTopLeftRadius: 2,
    borderTopRightRadius: 2,
  },
  groupLabel: {
    marginTop: 4,
    fontSize: 10,
    color: '#4B5563',
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
  },
  emptyText: {
    fontSize: 14,
    color: '#9CA3AF',
    fontWeight: '500',
  },
});

export default HoursGroupedBarCard;
